import { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isCancel } from 'axios';
import { useTranslation } from 'next-i18next';

import { isRateLimitedError } from '@/api/apiUtil';
import { SearchImagesProvider } from '@/api/gen/_model/searchImagesProvider';
import { searchImages } from '@/api/gen/image-search/image-search.gen';

import type {
  ImageSearchProvider,
  ImageSearchResult,
} from '../types/imageSearch.types';
import {
  type ImageSearchFailure,
  openImageSearchFailDialog,
} from '../utils/imageSearchFailDialog';
import { getPexelsLocale } from '../utils/pexelsLocale';

// 기존 Vercel 라우트 시절의 페이지 크기를 유지한다 (naver display=20, pexels per_page=50)
const IMAGES_PER_PAGE: Record<ImageSearchProvider, number> = {
  naver: 20,
  pexels: 50,
};

const PROVIDER_PARAM: Record<
  ImageSearchProvider,
  (typeof SearchImagesProvider)[keyof typeof SearchImagesProvider]
> = {
  naver: SearchImagesProvider.naver,
  pexels: SearchImagesProvider.pexels,
};

// react-query가 signal로 abort한 취소(CanceledError)는 제외해야 스크롤 중 abort마다 다이얼로그가 뜨지 않는다.
export const getImageSearchFailure = (
  error: unknown,
): ImageSearchFailure | null => {
  if (!error || isCancel(error)) return null;
  return isRateLimitedError(error) ? 'rate_limited' : 'failed';
};

interface UseImageSearchParams {
  provider: ImageSearchProvider;
  searchQuery: string;
  countryCode: string;
  enabled: boolean;
}

// orval은 infinite query 훅을 생성하지 않아 페이지네이션 래핑만 직접 유지하고,
// fetcher·타입은 생성 코드(searchImages, ImageSearchRes)를 사용한다.
export const useImageSearch = ({
  provider,
  searchQuery,
  countryCode,
  enabled,
}: UseImageSearchParams) => {
  const { t } = useTranslation(['build', 'common']);
  const locale =
    provider === 'pexels' ? getPexelsLocale(countryCode) : undefined;

  const query = useInfiniteQuery({
    queryKey: ['image-search', provider, searchQuery, locale],
    queryFn: async ({ pageParam, signal }): Promise<ImageSearchResult> => {
      const { data } = await searchImages(
        {
          provider: PROVIDER_PARAM[provider],
          query: searchQuery,
          page: pageParam,
          perPage: IMAGES_PER_PAGE[provider],
          locale,
        },
        undefined,
        signal,
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.nextPage ?? undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // 외부 API 타임아웃(502)을 재시도하면 실패 안내가 수십 초 늦어진다
    throwOnError: false,
    enabled: enabled && !!searchQuery.trim(),
  });

  useEffect(() => {
    const failure = getImageSearchFailure(query.error);
    if (failure) openImageSearchFailDialog(t, failure);
  }, [query.error, t]);

  return query;
};
